import { IconButton } from '@/components/ui/button.tsx';
import useFixRotationStore from '@modules/fixRotation/hook/useFixRotationStore.ts';
import { cn } from '@/lib/utils';
import { CheckCircle2, MousePointerClick } from 'lucide-react';

export function FixRotationUI() {
  const { rotationPhase, setRotationPhase, fixRotationService } = useFixRotationStore();

  const containerClasses =
    'absolute ml-8 top-100 z-20 w-full max-w-xl rounded-3xl border border-primary/40 bg-content/85 p-8 text-white shadow-2xl shadow-primary/25 backdrop-blur';

  const header = (
    <header className="flex flex-col gap-1 text-center">
      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-primary/70">Rotation alignment</p>
      <h2 className="text-2xl font-semibold tracking-tight">Ensure the jaw is oriented correctly</h2>
    </header>
  );

  if (rotationPhase === 'start') {
    return (
      <section className={containerClasses}>
        {header}
        <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-content-highlight/35 bg-content-dark/60 p-6 text-sm text-white/80">
          <p>
            We’ll guide you through placing three reference points so we can align the scan in 3D space. This only takes a
            moment and gives us the best result downstream.
          </p>
          <div className="flex items-center gap-3 rounded-2xl border border-primary/25 bg-primary/10 p-4 text-left text-sm">
            <MousePointerClick className="h-5 w-5 text-primary" />
            <span>Use the mouse to orbit the model and click when you’re ready to start selecting points.</span>
          </div>
        </div>

        <footer className="mt-6 flex justify-center">
          <IconButton
            className="font-semibold"
            size="lg"
            onClick={() => setRotationPhase('select')}
          >
            Start alignment
          </IconButton>
        </footer>
      </section>
    );
  }

  if (rotationPhase === 'select') {
    return (
      <section className={containerClasses}>
        {header}
        <div className="mt-6 rounded-3xl border border-content-highlight/35 bg-content-dark/60 p-6 text-sm text-white/80">
          <h3 className="text-left text-sm font-semibold uppercase tracking-[0.3em] text-white/55">Select three anatomical points</h3>
          <ul className="mt-4 space-y-3 text-left text-base text-white/85">
            <li className="rounded-2xl border border-content-highlight/30 bg-content/40 p-3">
              <strong className="text-primary">Front tooth apex:</strong> pick the highest point on a front tooth (center).
            </li>
            <li className="rounded-2xl border border-content-highlight/30 bg-content/40 p-3">
              <strong className="text-primary">Left molar peak:</strong> select the highest point on a back left molar.
            </li>
            <li className="rounded-2xl border border-content-highlight/30 bg-content/40 p-3">
              <strong className="text-primary">Right molar peak:</strong> select the highest point on a back right molar.
            </li>
          </ul>
          <p className="mt-5 text-center text-xs uppercase tracking-[0.3em] text-white/45">
            These points define the jaw plane for alignment
          </p>
        </div>

        <footer className="mt-6 flex justify-center">
          <IconButton
            size="lg"
            className="font-semibold"
            onClick={() => fixRotationService?.fixRotation()}
          >
            Apply rotation
          </IconButton>
        </footer>
      </section>
    );
  }

  if (rotationPhase === 'fixed') {
    return (
      <section className={cn(containerClasses, 'max-w-md text-center')}>
        {header}
        <div className="mt-6 flex flex-col items-center gap-3 rounded-3xl border border-emerald-500/40 bg-emerald-500/15 p-6 text-emerald-100">
          <CheckCircle2 className="h-10 w-10" />
          <p className="text-base font-semibold">Rotation fixed!</p>
          <p className="text-sm text-emerald-100/80">
            The jaw orientation has been updated. You can proceed to upload or make additional adjustments.
          </p>
        </div>
        <footer className="mt-6 flex justify-center">
          <IconButton
            className="font-semibold"
            size="lg"
            onClick={() => setRotationPhase('done')}
          >
            Continue
          </IconButton>
        </footer>
      </section>
    );
  }

  return null;
}
